// src/components/settings/FullReductionSettingsCard.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import { fullReductionApi } from '../../api';

// 满减设置卡片：总开关+VIP参与开关是 shop_settings 的一部分，跟着页面主"保存设置"
// 按钮一起存（settings.full_reduction_enabled / settings.full_reduction_vip_allowed，
// 由父组件 ShopSettingsPage 管理）；阶梯规则本身存在独立的 full_reduction_tiers 表，
// 走单独的接口，所以这张卡片自己单独管理阶梯列表的状态，配一个独立的"保存规则"按钮，
// 不和外层的"保存设置"混在一起——避免用户以为点了外层按钮阶梯规则也保存了
const FullReductionSettingsCard = ({ settings, onToggle }) => {
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [savingTiers, setSavingTiers] = useState(false);
  const [tierError, setTierError] = useState('');
  const [tierSuccess, setTierSuccess] = useState('');

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    setLoadingTiers(true);
    try {
      const res = await fullReductionApi.getTiers();
      if (res.success) setTiers(res.data.length > 0 ? res.data : [{ threshold_amount: '', discount_amount: '' }]);
      else setTiers([{ threshold_amount: '', discount_amount: '' }]);
    } catch (err) {
      setTiers([{ threshold_amount: '', discount_amount: '' }]);
    } finally {
      setLoadingTiers(false);
    }
  };

  const addTierRow = () => {
    setTiers(prev => [...prev, { threshold_amount: '', discount_amount: '' }]);
  };

  const removeTierRow = (index) => {
    setTiers(prev => prev.filter((_, i) => i !== index));
  };

  const updateTierRow = (index, field, value) => {
    setTiers(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const handleSaveTiers = async () => {
    setTierError('');
    setTierSuccess('');

    // 过滤掉两个字段都没填的空行（比如新加了一行还没填就点保存）
    const validTiers = tiers.filter(t => t.threshold_amount !== '' || t.discount_amount !== '');
    for (const t of validTiers) {
      if (!t.threshold_amount || !t.discount_amount) {
        setTierError('请把每一档的门槛金额和减免金额都填完整');
        return;
      }
    }

    setSavingTiers(true);
    try {
      const res = await fullReductionApi.saveTiers({ tiers: validTiers });
      if (res.success) {
        setTierSuccess('满减规则已保存');
        setTimeout(() => setTierSuccess(''), 3000);
        loadTiers();
      } else {
        setTierError(res.message || '保存失败');
      }
    } catch (err) {
      setTierError(err.message || '保存失败');
    } finally {
      setSavingTiers(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-800">满减设置</h3>
          <p className="text-sm text-gray-500 mt-1">设置阶梯式满减规则，客户下单自动匹配最高档</p>
        </div>
        <button
          onClick={() => onToggle('full_reduction_enabled')}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ml-4 ${
            settings.full_reduction_enabled ? 'bg-orange-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              settings.full_reduction_enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {settings.full_reduction_enabled && (
        <>
          <label className="flex items-center justify-between py-2 border-t border-gray-100">
            <div>
              <span className="text-sm text-gray-700">VIP客户参与满减</span>
              <p className="text-xs text-gray-400 mt-0.5">关闭后，VIP客户下单不再享受满减（VIP已经有VIP价，避免优惠叠得太多）</p>
            </div>
            <button
              onClick={() => onToggle('full_reduction_vip_allowed')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ml-4 ${
                settings.full_reduction_vip_allowed ? 'bg-orange-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.full_reduction_vip_allowed ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>

          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">阶梯规则</span>
              <button onClick={addTierRow}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
                <Plus className="w-4 h-4" />添加一档
              </button>
            </div>

            {loadingTiers ? (
              <p className="text-sm text-gray-400 py-4 text-center">加载中...</p>
            ) : (
              <div className="space-y-2">
                {tiers.map((tier, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 whitespace-nowrap">满</span>
                    <input type="number" step="0.01" min="0" placeholder="金额"
                      value={tier.threshold_amount}
                      onChange={e => updateTierRow(index, 'threshold_amount', e.target.value)}
                      className="w-24 px-2 py-1.5 border rounded-lg text-sm" />
                    <span className="text-sm text-gray-500 whitespace-nowrap">减</span>
                    <input type="number" step="0.01" min="0" placeholder="金额"
                      value={tier.discount_amount}
                      onChange={e => updateTierRow(index, 'discount_amount', e.target.value)}
                      className="w-24 px-2 py-1.5 border rounded-lg text-sm" />
                    <button onClick={() => removeTierRow(index)}
                      className="text-red-400 hover:text-red-600 ml-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {tierError && <p className="text-sm text-red-500 mt-2">{tierError}</p>}
            {tierSuccess && <p className="text-sm text-green-600 mt-2">{tierSuccess}</p>}

            <button onClick={handleSaveTiers} disabled={savingTiers || loadingTiers}
              className="flex items-center gap-1.5 mt-3 bg-orange-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-600 disabled:opacity-50">
              <Save className="w-4 h-4" />
              {savingTiers ? '保存中...' : '保存满减规则'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FullReductionSettingsCard;